import { AfterViewInit, Directive, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CommandBase, DeleteCommand, FilteredCommand, standardCommand } from '../../model/command';
import { ColumnDefinition } from '../../model/column-definition';
import { MenuItemDefinition } from '../../model/menu-item-definition';
import { ModelBase } from '../../model/model-base';
import { DataService } from '../../service/base/data.service';
import { CommandDispatcher } from '../../service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../service/command/command-handler-resolver.service';
import { NavigationService } from '../../service/navigation.service';
import { CommandController } from '../base/command-controller.base';
import { MessageService } from './../../../core/service/message.service';
import { FilterDefinitionItem } from '../../model/filter-definition-item';
import { FilterComponent } from '../list/filter/filter.component';
import { Subscription } from 'rxjs';
import { SidebarService } from '../../service/sidebar.service';

export interface SidebarMenuAction<TCommand extends CommandBase = CommandBase> {
  commandId: string;
  template: TemplateRef<any>;
  createDataContext?: (command: TCommand) => any;
  onClose?: () => void;
}

@Directive()
export abstract class ObjectManagerComponentBase<TModel extends ModelBase> extends CommandController implements AfterViewInit, OnInit {
  constructor(
    protected dataService: DataService<TModel>,
    protected messageService: MessageService,
    protected navigation: NavigationService,
    commandDispatcher: CommandDispatcher,
    handlerResolver: CommandHandlerResolver,
    protected sidebarService?: SidebarService
  ) {
    super(commandDispatcher, handlerResolver);
  }

  @ViewChild(FilterComponent) filterComponent: FilterComponent;
  @Input() preferencesKey: string;

  allowNew: boolean = true;
  allowOpenOnRowClick: boolean = true;
  columnDefinition: ColumnDefinition[];
  contextMenuDefinition: MenuItemDefinition[] = [];
  filter: FormGroup;
  filterDefinition: FilterDefinitionItem[];
  defaultFilters: string[];
  itemsSource: TModel[];
  title: string;
  filterState: any;
  sidebarMenuActionCommandIds: string[] = [];
  sidebarMenuDataContext: any;
  private sidebarMenuActions: SidebarMenuAction[] = [];

  private _coerceDelete(command: CommandBase) {
    if (command instanceof DeleteCommand) {
      command.dataService = this.dataService;
      command.itemsSource = this.itemsSource;
    } else if (command instanceof FilteredCommand) {
      command.dataService = this.dataService;
      command.filterProvider = this;
    }
  }

  protected addContextMenuItemDefinition(caption: string, commandName: string, disableItem?: (context: any) => boolean): void {
    if (!this.contextMenuDefinition.find(item => item.commandId == commandName))
      this.contextMenuDefinition.push(new MenuItemDefinition(caption, commandName, null, disableItem));
  }

  protected addEditDeleteCommands(disableDelete?: (context: any) => boolean) {
    if (this.allowOpenOnRowClick) {
      this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
    }
    this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete, disableDelete);
  }

  canExecute(command: CommandBase): boolean {
    this._coerceDelete(command);
    return super.canExecute(command);
  }

  protected clearAdditionalSubscriptions() {
    super.clearAdditionalSubscriptions();
    if (this.filterSubscription) this.filterSubscription.unsubscribe();
  }

  protected clearTimezoneOffset(date: Date): Date {
    if (!date) return null;

    date = this.coerceDate(date);
    const offset = date.getTimezoneOffset() / 60;
    date.setHours(date.getHours() - offset);
    return date;
  }

  protected coerceDate(date: string | Date) {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    return date;
  }

  execute(command: CommandBase) {
    this._coerceDelete(command);
    super.execute(command);
  }

  registerSidebarMenuAction(action: SidebarMenuAction): void {
    if (!action?.commandId || !action.template) return;

    this.sidebarMenuActions = this.sidebarMenuActions.filter(item => item.commandId !== action.commandId);
    this.sidebarMenuActions.push(action);

    if (!this.sidebarMenuActionCommandIds.includes(action.commandId)) {
      this.sidebarMenuActionCommandIds = [...this.sidebarMenuActionCommandIds, action.commandId];
    }
  }

  onMenuCommand(command: CommandBase): void {
    this._coerceDelete(command);
    const action = this.sidebarMenuActions.find(item => item.commandId === command.id);
    if (!action) {
      this.execute(command);
      return;
    }

    if (!this.sidebarService) {
      this.messageService.showError('Sidebar service is not available.');
      return;
    }

    this.sidebarMenuDataContext = action.createDataContext ? action.createDataContext(command) : command;
    this.sidebarService.setSidebarContent({
      template: action.template,
      onClose: () => {
        action.onClose?.();
        this.sidebarMenuDataContext = null;
      },
    });
    this.sidebarService.toggleSidebar(true);
  }

  closeSidebarMenuAction(): void {
    this.sidebarService?.toggleSidebar(false);
  }

  ngAfterViewInit() {
    window.scrollTo(0, 0);
  }

  ngOnInit() {
    if (!this.filter) {
      this.AddSubscription(this.dataService.getList().subscribe(data => (this.itemsSource = data)));
    }
    this.addEditDeleteCommands();
  }

  onCreateItem() {
    this.navigation.navigateToForm();
  }

  onFilter() {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
    if (this.filter) {
      this.filterSubscription = this.dataService.getFilteredList(this.filter.value).subscribe(
        items => (this.itemsSource = items),
        error => this.messageService.showError(error)
      );
    } else {
      this.filterSubscription = this.dataService.getList().subscribe(
        list => (this.itemsSource = list),
        error => this.messageService.showError(error)
      );
    }
  }

  private filterSubscription: Subscription;

  protected reloadCurrentList(): void {
    if (this.filterComponent) {
      const selectedFields = this.filterComponent.filters?.filter(filter => filter.selected).map(filter => filter.name);
      this.onFilterChange({
        ...(this.filterComponent.form?.value ?? {}),
        selectedFields: selectedFields ?? this.defaultFilters ?? [],
      });
    } else {
      this.onFilter();
    }
  }

  onFilterChange(filterState: any) {
    if (!filterState.selectedFields || this.filterDefinition === undefined) return;

    this.filterState = filterState;

    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    this.filterInternal(filterState);

    this.filterSubscription = this.dataService.getFilteredList(filterState).subscribe(
      items => (this.itemsSource = items),
      error => this.messageService.showError(error)
    );
  }

  filterInternal(filterState: any) {
    return;
  }

  onRowClicked(e: { context: any; event: any }) {
    this.navigation.navigateToForm(e.context.id, e.event.ctrlKey);
  }
}
